import InfoTooltip from "~/components/reusable/InfoTooltip"
import { cn } from "~/utils/helpers"

const TONE = {
  good: "text-badge-green-fg-text",
  warn: "text-badge-red-fg-text",
  muted: "text-fg-muted",
} as const

/**
 * Label left, number right: the only readout primitive for inspectors, cards and hover cards.
 * Replaces the near-identical local `Row` components those surfaces each declared.
 *
 * `weight`: default stresses the value, `muted` de-emphasises it, `emphasis` promotes the label too
 * for a total row. `tone` colours the value only. `value` may be a `StatusBadge` for a stage.
 *
 * Approved surface: design-system/APPROVED.md.
 */
export const StatRow = ({
  label,
  value,
  icon,
  sub,
  info,
  tone,
  weight,
  className,
}: {
  label: string
  value: React.ReactNode
  icon?: React.ReactNode
  /** Secondary qualifier appended to the label after a separator dot. */
  sub?: string
  /** Renders an InfoTooltip after the label. */
  info?: string
  tone?: keyof typeof TONE
  weight?: "muted" | "emphasis"
  className?: string
}) => (
  <div className={cn("flex items-baseline justify-between", className)}>
    <span
      className={cn(
        weight === "emphasis" ? "label-sm" : "paragraph-sm text-fg-subtle",
        (icon || info) && "flex items-center gap-1.5",
      )}
    >
      {icon}
      {label}
      {sub && <span className="text-fg-muted"> · {sub}</span>}
      {info && <InfoTooltip title={info} className="size-3" />}
    </span>
    <span
      className={cn(
        weight === "muted" ? "paragraph-sm" : "label-sm",
        "tabular-nums",
        tone && TONE[tone],
      )}
    >
      {value}
    </span>
  </div>
)
